import { useCallback, useContext } from "react";
import { useIntl } from "react-intl";

import { NotificationsContext } from "@ui-components/Notifications";

import CountryContext from "../../../../CountryContext";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";

const useCanvasState = (canvas, taskId, taskMapId, initialState=null) => {
  const intl = useIntl();
  // contexts
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);

  const countryApiRequest = useCountryAxiosInstance(country.code);

  const serializeCanvasState = useCallback(() => {
    if (!canvas) return null;
    const state = canvas.toJSON(["name"]); // Serialize the canvas state
    delete state["backgroundImage"];
    return state;
  }, [canvas]);

  const saveCanvasState = useCallback(
    async (canvasState) => {
      if (!canvasState || !countryApiRequest || !taskMapId || !taskId) return;

      try {
        await countryApiRequest.post(`/worksites_tasks_maps_data/`, {
          id_task: taskId,
          id_taskmap: taskMapId,
          canvas_data: canvasState,
        });
      } catch (error) {
        push({
          title: intl.formatMessage({ id: "server_error" }),
          type: "error",
        });
      }
    },
    [countryApiRequest, taskId, taskMapId, intl, push]
  );

  const fetchCanvasState = useCallback(async () => {
    if (initialState) {
      return initialState;
    }
    if (taskId && taskMapId) {
      const response = await countryApiRequest.get(
        `/worksites_tasks_maps_data/taskid/${taskId}`
        );
      const data = response?.canvas_data;
      if (data) {
        data['tmd'] = response;
      }
      return data;
    } else if (taskMapId) {
      const response = await countryApiRequest.get(
        `/worksites_tasks_maps_data/taskmapid/${taskMapId}`
      );
      if (response && response.length > 0) {
        const objects = response.flatMap((tmd) => {
          const objects = tmd.canvas_data.objects.map((o) => {
            return {
              ...o,
              tmd
            }
          })
          // uncomment the following to show only pins and balloons
          // on the global task map
          /*.filter((o) =>
          ["pin", "balloon"].includes(o?.name)
          ).map((o) => {
            return {
              ...o,
              tmd
            }
          })*/
          return objects;
        });

        const version = response[0]?.canvas_data.version;
        const background = response[0]?.canvas_data.background;
        return {
          version,
          background,
          objects,
        };
      }
    }
    return {};
  }, [countryApiRequest, taskId, taskMapId, initialState]);

  return { serializeCanvasState, saveCanvasState, fetchCanvasState };
};

export default useCanvasState;
